import React, { useState, useEffect, useRef } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  IconButton,
  Tooltip,
  Button,
  Chip,
  MenuItem,
  TextField,
} from "@mui/material";
import { TableSortLabel } from "@mui/material";
import {
  getComparator,
  stableSort,
} from "../../../../common-utilities/sortingUtils";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import DeleteConfirmation from "../../common/DeleteConfirmation";
import { NotificationManager } from "react-notifications";
import { GetAllCoolers, DeleteCooler } from "../../../services/CoolerService";
import ViewCooler from "./ViewCooler";
import CommonLoader from "../../common/CommonLoader";
import { useNavigate } from "react-router-dom";

const statusOptions = [
  { value: "", label: "All" },
  { value: "CheckedIn", label: "Checked In" },
  { value: "CheckedOut", label: "Checked Out" },
];

const CoolersTable = () => {
  const columns = [
    { id: "id", label: "ID" },
    { id: "coolerId", label: "Cooler ID" },
    { id: "locationName", label: "Location" },
    { id: "profile", label: "Profile" },
    { id: "isCheckedIn", label: "Status" },
    { id: "clientAccountNumber", label: "Client #" },
    { id: "lastCheckInTime", label: "Last Check-In" },
    { id: "actions", label: "Actions" },
  ];

  const rowsPerPageOptions = [10, 25, 50, 100, 200];

  const [coolerData, setCoolerData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isDeleted, setIsDeleted] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[1]);
  const [selectedCooler, setSelectedCooler] = useState(null);
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] =
    useState(false);
  const [orderBy, setOrderBy] = useState("");
  const [order, setOrder] = useState("asc");
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [tableHeight, setTableHeight] = useState(600);

  const tableContainerRef = useRef(null);
  const navigate = useNavigate();

  // Dynamic table height calculation
  useEffect(() => {
    const calculateTableHeight = () => {
      if (tableContainerRef.current) {
        const containerTop =
          tableContainerRef.current.getBoundingClientRect().top;
        const windowHeight = window.innerHeight;
        const availableHeight = windowHeight - containerTop - 76;
        const calculatedHeight = Math.max(
          300,
          Math.min(availableHeight, windowHeight - 200)
        );
        setTableHeight(calculatedHeight);
      }
    };

    calculateTableHeight();
    window.addEventListener("resize", calculateTableHeight);
    const timeoutId = setTimeout(calculateTableHeight, 100);

    return () => {
      window.removeEventListener("resize", calculateTableHeight);
      clearTimeout(timeoutId);
    };
  }, [filteredData]);

  useEffect(() => {
    fetchCoolers();
  }, [isDeleted]);

  useEffect(() => {
    applyFilters(coolerData, searchText, statusFilter);
  }, [searchText, statusFilter, coolerData]);

  const applyFilters = (data, search, status) => {
    let result = [...data];

    if (search) {
      const q = search.toLowerCase();
      result = result.filter(
        (r) =>
          (r.coolerId || "").toLowerCase().includes(q) ||
          (r.locationName || "").toLowerCase().includes(q) ||
          (r.coolerProfile?.profileName || "").toLowerCase().includes(q) ||
          String(r.clientAccountNumber || "").includes(q)
      );
    }

    if (status === "CheckedIn") {
      result = result.filter((r) => r.isCheckedIn === true);
    } else if (status === "CheckedOut") {
      result = result.filter((r) => r.isCheckedIn === false);
    }

    setFilteredData(result);
  };

  const fetchCoolers = async () => {
    try {
      setLoading(true);
      const response = await GetAllCoolers();
      const data = response?.results || [];
      setCoolerData(data);
      applyFilters(data, searchText, statusFilter);
    } catch (error) {
      console.error("Error fetching coolers:", error);
      NotificationManager.error("Error fetching coolers");
    } finally {
      setLoading(false);
    }
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewClick = (cooler) => setSelectedCooler(cooler);

  const handleBackClick = () => setSelectedCooler(null);

  const handleDeleteClick = (cooler) => {
    setSelectedCooler(cooler);
    setIsDeleteConfirmationOpen(true);
  };

  const handleDeleteConfirmationClose = () => {
    setIsDeleteConfirmationOpen(false);
    setSelectedCooler(null);
  };

  const handleDeleteConfirmationConfirm = async () => {
    try {
      setLoading(true);
      const response = await DeleteCooler(selectedCooler.id);
      if (response.results === true) {
        NotificationManager.success("Cooler deleted successfully", "Success");
        setIsDeleted(!isDeleted);
      } else {
        NotificationManager.error("Error deleting cooler", "Error");
      }
    } catch (error) {
      NotificationManager.error("Error deleting cooler", "Error");
    } finally {
      setLoading(false);
      setSelectedCooler(null);
      setIsDeleteConfirmationOpen(false);
    }
  };

  const handleEditClick = (cooler) => navigate(`/coolers/${cooler.id}`);

  const renderCell = (col, cooler) => {
    if (col.id === "profile")
      return cooler.coolerProfile?.profileName || "N/A";
    if (col.id === "isCheckedIn")
      return (
        <Chip
          label={cooler.isCheckedIn ? "Checked In" : "Checked Out"}
          color={cooler.isCheckedIn ? "success" : "warning"}
          size="small"
        />
      );
    if (col.id === "lastCheckInTime")
      return cooler.lastCheckInTime
        ? new Date(cooler.lastCheckInTime).toLocaleString([], {
          year: "numeric",
          month: "short",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
        })
        : "N/A";
    return cooler[col.id] != null ? cooler[col.id] : "N/A";
  };

  const renderActions = (cooler) => (
    <div style={{ minWidth: "100px", display: "flex" }}>
      <Tooltip title="Edit Cooler">
        <IconButton
          style={{ color: "orange" }}
          onClick={() => handleEditClick(cooler)}
        >
          <EditIcon />
        </IconButton>
      </Tooltip>

      <Tooltip title="View Cooler">
        <IconButton
          style={{ color: "green" }}
          onClick={() => handleViewClick(cooler)}
        >
          <VisibilityIcon />
        </IconButton>
      </Tooltip>

      <Tooltip title="Delete Cooler">
        <IconButton
          style={{ color: "red" }}
          onClick={() => handleDeleteClick(cooler)}
        >
          <DeleteIcon />
        </IconButton>
      </Tooltip>
    </div>
  );

  return (
    <>
      <span className="title">Coolers</span>
      <hr />

      {selectedCooler ? (
        <>
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            onClick={handleBackClick}
            style={{ marginBottom: "16px", color: "green" }}
          >
            Back
          </Button>
          <ViewCooler cooler={selectedCooler} />
        </>
      ) : (
        <>
          <div
            style={{
              marginBottom: "16px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 8,
            }}
          >

            <TextField
              label="Search"
              variant="outlined"
              size="small"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ flex: 1, maxWidth: "400px" }}
            />
            <Button
              variant="outlined"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => navigate("/coolers/add")}
            >
              Add Cooler
            </Button>
          </div>

          <Paper>
            <TableContainer
              ref={tableContainerRef}
              sx={{ maxHeight: tableHeight, overflowY: "auto", width: "99%" }}
            >
              <Table size="small" stickyHeader>
                <TableHead
                  sx={{
                    backgroundColor: (theme) => theme.palette.primary.main,
                    color: "white",
                  }}
                >
                  <TableRow>
                    {columns.map((col) => (
                      <TableCell key={col.id}>
                        {col.id === "actions" || col.id === "isCheckedIn" ? (
                          col.label
                        ) : (
                          <TableSortLabel
                            active={orderBy === col.id}
                            direction={orderBy === col.id ? order : "asc"}
                            onClick={() => handleRequestSort(col.id)}
                          >
                            {col.label}
                          </TableSortLabel>
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {stableSort(filteredData, getComparator(order, orderBy))
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((cooler, index) => (
                      <TableRow
                        key={cooler.id}
                        sx={{
                          backgroundColor:
                            index % 2 === 0 ? "rgba(0,0,0,0.04)" : "white",
                        }}
                      >
                        {columns.map((col) => (
                          <TableCell key={col.id}>
                            {col.id === "actions"
                              ? renderActions(cooler)
                              : renderCell(col, cooler)}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              rowsPerPageOptions={rowsPerPageOptions}
              component="div"
              count={filteredData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Paper>
        </>
      )}

      <DeleteConfirmation
        isOpen={isDeleteConfirmationOpen}
        onClose={handleDeleteConfirmationClose}
        onConfirm={handleDeleteConfirmationConfirm}
        message="Are you sure you want to delete this cooler?"
      />

      <CommonLoader loading={loading} />
    </>
  );
};

export default CoolersTable;